/**
 * Encryption Utility for PHI/PII Data
 * 
 * This module provides client-side encryption for sensitive data before
 * storing in Firestore. Uses AES-256-GCM via the Web Crypto API.
 * 
 * IMPORTANT: The encryption key should be stored securely and rotated periodically.
 * For production, consider using Google Cloud KMS or a dedicated key management service.
 */

// Fields that should always be encrypted when present
export const PHI_FIELDS = [
  'patientID',
  'patientId',
  'patientNames',
  'patientName',
  'emergencies',
  'treatmentOptions',
  'incidentDescription',
  'complicationDetails',
  'procedureDetails',
  'medicalHistory',
  'diagnosis',
  'symptoms',
  'medications',
  'allergies',
  'vitalSigns',
  'labResults',
  'imagingResults',
  'treatmentPlan',
  'clinicalNotes'
];

export const PII_FIELDS = [
  'firstName',
  'lastName',
  'fullName',
  'email',
  'mobile',
  'phone',
  'address',
  'ssn',
  'dateOfBirth',
  'dob'
];

export const CREDENTIAL_FIELDS = [
  'medicalLicense',
  'dentalLicense',
  'deaCertification',
  'npiNumber',
  'stateControlledSubstance',
  'boardCertification'
];

// Encryption key - loaded from environment variables
// IMPORTANT: Set REACT_APP_ENCRYPTION_KEY in your .env file (and in production build env)
// Generate a key with: openssl rand -base64 32
const DEFAULT_KEY = 'AlliedOMS-Dev-Key-NOT-FOR-PROD-32';
let _keyWarningShown = false;

const keyStringToCryptoKey = async (keyString) => {
  const keyMaterial = new TextEncoder().encode(keyString);
  const paddedKey = new Uint8Array(32);
  paddedKey.set(keyMaterial.slice(0, 32));
  if (keyMaterial.length < 32) {
    for (let i = keyMaterial.length; i < 32; i++) paddedKey[i] = i;
  }
  return crypto.subtle.importKey('raw', paddedKey, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
};

const getEncryptionKey = async () => {
  const envKey = process.env.REACT_APP_ENCRYPTION_KEY;
  if (!envKey) {
    if (!_keyWarningShown) {
      _keyWarningShown = true;
      console.warn(
        '⚠️ SECURITY WARNING: No encryption key found in environment variables.\n' +
        'Using default key - DO NOT USE IN PRODUCTION!\n' +
        'Set REACT_APP_ENCRYPTION_KEY in your .env file and in your production build environment.'
      );
    }
  }
  return keyStringToCryptoKey(envKey || DEFAULT_KEY);
};

// Fallback key for decrypting legacy data encrypted with the default key
// (e.g. before REACT_APP_ENCRYPTION_KEY was set). Enables key migration without data loss.
const getFallbackDecryptionKey = async () => {
  return keyStringToCryptoKey(DEFAULT_KEY);
};

/**
 * Encrypts a string value using AES-256-GCM
 * @param {string} plaintext - The text to encrypt
 * @returns {Promise<string>} - Base64 encoded encrypted string with IV
 */
export const encryptValue = async (plaintext) => {
  // Handle null/undefined
  if (plaintext === null || plaintext === undefined) {
    return plaintext;
  }
  
  // Convert numbers and other types to strings for encryption
  const textToEncrypt = typeof plaintext === 'string' ? plaintext : String(plaintext);
  
  // Skip empty strings
  if (!textToEncrypt || textToEncrypt.length === 0) {
    return plaintext;
  }
  
  try {
    const key = await getEncryptionKey();
    const iv = crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV for GCM
    const encodedText = new TextEncoder().encode(textToEncrypt);
    
    const encryptedBuffer = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      encodedText
    );
    
    // Combine IV and encrypted data, then base64 encode
    const combined = new Uint8Array(iv.length + encryptedBuffer.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encryptedBuffer), iv.length);
    
    return 'ENC:' + btoa(String.fromCharCode(...combined));
  } catch (error) {
    console.error('Encryption error:', error);
    throw new Error('Failed to encrypt data');
  }
};

/**
 * Decrypts an AES-256-GCM encrypted string
 * Tries primary key first, then fallback key (default) for legacy data encrypted before key rotation
 * @param {string} ciphertext - Base64 encoded encrypted string with IV
 * @returns {Promise<string>} - Decrypted plaintext
 */
export const decryptValue = async (ciphertext) => {
  if (!ciphertext || typeof ciphertext !== 'string') {
    return ciphertext;
  }

  if (!ciphertext.startsWith('ENC:')) {
    return ciphertext; // Return as-is if not encrypted
  }

  const encryptedData = ciphertext.slice(4);
  const combined = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));
  const iv = combined.slice(0, 12);
  const data = combined.slice(12);

  const tryDecrypt = async (key) => {
    const decryptedBuffer = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      data
    );
    return new TextDecoder().decode(decryptedBuffer);
  };

  try {
    const key = await getEncryptionKey();
    return await tryDecrypt(key);
  } catch (primaryError) {
    // Wrong key (OperationError) - try fallback for legacy data encrypted with default key
    const envKey = process.env.REACT_APP_ENCRYPTION_KEY;
    if (envKey) {
      try {
        const fallbackKey = await getFallbackDecryptionKey();
        return await tryDecrypt(fallbackKey);
      } catch {
        // Fallback also failed - rethrow original
      }
    }
    console.error('Decryption error:', primaryError);
    throw new Error('Failed to decrypt data');
  }
};

/**
 * Encrypts an array of values
 * @param {Array} array - Array of strings to encrypt
 * @returns {Promise<Array>} - Array of encrypted strings
 */
export const encryptArray = async (array) => {
  if (!Array.isArray(array)) {
    return array;
  }
  
  return Promise.all(array.map(item => {
    // Encrypt strings and numbers
    if (typeof item === 'string' || typeof item === 'number') {
      return encryptValue(item);
    }
    return item;
  }));
};

/**
 * Decrypts an array of values
 * @param {Array} array - Array of encrypted strings
 * @returns {Promise<Array>} - Array of decrypted strings
 */
export const decryptArray = async (array) => {
  if (!Array.isArray(array)) {
    return array;
  }
  
  return Promise.all(array.map(item => {
    if (typeof item === 'string' && item.startsWith('ENC:')) {
      return decryptValue(item);
    }
    return item;
  }));
};

/**
 * Encrypts specified fields in an object
 * @param {Object} data - Object containing data to encrypt
 * @param {Array} fieldsToEncrypt - Array of field names to encrypt
 * @returns {Promise<Object>} - Object with encrypted fields
 */
export const encryptFields = async (data, fieldsToEncrypt = PHI_FIELDS) => {
  if (!data || typeof data !== 'object') {
    return data;
  }
  
  const encryptedData = { ...data };
  
  for (const field of fieldsToEncrypt) {
    if (encryptedData[field] !== undefined && encryptedData[field] !== null) {
      if (Array.isArray(encryptedData[field])) {
        encryptedData[field] = await encryptArray(encryptedData[field]);
      } else if (typeof encryptedData[field] === 'string' || typeof encryptedData[field] === 'number') {
        // Encrypt strings and numbers (encryptValue handles conversion)
        encryptedData[field] = await encryptValue(encryptedData[field]);
      } else if (typeof encryptedData[field] === 'object') {
        // Handle nested objects (like credentials)
        encryptedData[field] = await encryptNestedObject(encryptedData[field]);
      }
    }
  }
  
  // Add metadata about encryption
  encryptedData._encrypted = {
    version: '1.0',
    timestamp: new Date().toISOString(),
    fields: fieldsToEncrypt.filter(f => encryptedData[f] !== undefined)
  };
  
  return encryptedData;
};

/**
 * Decrypts specified fields in an object
 * @param {Object} data - Object containing encrypted data
 * @param {Array} fieldsToDecrypt - Array of field names to decrypt
 * @returns {Promise<Object>} - Object with decrypted fields
 */
export const decryptFields = async (data, fieldsToDecrypt = PHI_FIELDS) => {
  if (!data || typeof data !== 'object') {
    return data;
  }
  
  const decryptedData = { ...data };
  
  for (const field of fieldsToDecrypt) {
    if (decryptedData[field] !== undefined && decryptedData[field] !== null) {
      if (Array.isArray(decryptedData[field])) {
        decryptedData[field] = await decryptArray(decryptedData[field]);
      } else if (typeof decryptedData[field] === 'string') {
        decryptedData[field] = await decryptValue(decryptedData[field]);
      } else if (typeof decryptedData[field] === 'object') {
        // Handle nested objects
        decryptedData[field] = await decryptNestedObject(decryptedData[field]);
      }
    }
  }
  
  return decryptedData;
};

/**
 * Encrypts values in a nested object (for credentials, etc.)
 * @param {Object} obj - Nested object to encrypt
 * @returns {Promise<Object>} - Encrypted nested object
 */
const encryptNestedObject = async (obj) => {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }
  
  const encrypted = {};
  
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string' && value.length > 0) {
      // Encrypt string values that look like sensitive data
      if (key.toLowerCase().includes('number') || 
          key.toLowerCase().includes('license') ||
          key.toLowerCase().includes('ssn') ||
          key.toLowerCase().includes('dea') ||
          key.toLowerCase().includes('npi')) {
        encrypted[key] = await encryptValue(value);
      } else {
        encrypted[key] = value;
      }
    } else if (typeof value === 'object' && value !== null) {
      encrypted[key] = await encryptNestedObject(value);
    } else {
      encrypted[key] = value;
    }
  }
  
  return encrypted;
};

/**
 * Decrypts values in a nested object
 * @param {Object} obj - Nested object to decrypt
 * @returns {Promise<Object>} - Decrypted nested object
 */
const decryptNestedObject = async (obj) => {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }
  
  const decrypted = {};
  
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string' && value.startsWith('ENC:')) {
      decrypted[key] = await decryptValue(value);
    } else if (typeof value === 'object' && value !== null) {
      decrypted[key] = await decryptNestedObject(value);
    } else {
      decrypted[key] = value;
    }
  }
  
  return decrypted;
};

/**
 * Encrypts PHI fields in form data before submission
 * @param {Object} formData - Form data object
 * @returns {Promise<Object>} - Form data with PHI encrypted
 */
export const encryptFormData = async (formData) => {
  const allSensitiveFields = [...PHI_FIELDS, ...PII_FIELDS];
  return encryptFields(formData, allSensitiveFields);
};

/**
 * Encrypts user credentials before storage
 * @param {Object} credentials - User credentials object
 * @returns {Promise<Object>} - Credentials with sensitive values encrypted
 */
export const encryptCredentials = async (credentials) => {
  if (!credentials || typeof credentials !== 'object') {
    return credentials;
  }
  
  const encrypted = {};
  
  for (const [key, value] of Object.entries(credentials)) {
    if (value && typeof value === 'object') {
      encrypted[key] = {};
      for (const [subKey, subValue] of Object.entries(value)) {
        if (subKey === 'number' && typeof subValue === 'string' && subValue.length > 0) {
          encrypted[key][subKey] = await encryptValue(subValue);
        } else {
          encrypted[key][subKey] = subValue;
        }
      }
    } else {
      encrypted[key] = value;
    }
  }
  
  return encrypted;
};

/**
 * Decrypts user credentials after retrieval
 * @param {Object} credentials - Encrypted credentials object
 * @returns {Promise<Object>} - Decrypted credentials
 */
export const decryptCredentials = async (credentials) => {
  if (!credentials || typeof credentials !== 'object') {
    return credentials;
  }
  
  const decrypted = {};
  
  for (const [key, value] of Object.entries(credentials)) {
    if (value && typeof value === 'object') {
      decrypted[key] = {};
      for (const [subKey, subValue] of Object.entries(value)) {
        if (subKey === 'number' && typeof subValue === 'string' && subValue.startsWith('ENC:')) {
          decrypted[key][subKey] = await decryptValue(subValue);
        } else {
          decrypted[key][subKey] = subValue;
        }
      }
    } else {
      decrypted[key] = value;
    }
  }
  
  return decrypted;
};

/**
 * Masks a sensitive value for display (e.g., "***-**-1234")
 * @param {string} value - Value to mask
 * @param {number} visibleChars - Number of characters to show at the end
 * @returns {string} - Masked value
 */
export const maskValue = (value, visibleChars = 4) => {
  if (!value || typeof value !== 'string') {
    return value;
  }
  
  if (value.length <= visibleChars) {
    return '*'.repeat(value.length);
  }
  
  const masked = '*'.repeat(value.length - visibleChars);
  const visible = value.slice(-visibleChars);
  return masked + visible;
};

/**
 * Checks if a value is encrypted
 * @param {string} value - Value to check
 * @returns {boolean} - True if encrypted
 */
export const isEncrypted = (value) => {
  return typeof value === 'string' && value.startsWith('ENC:');
};

/**
 * Decrypts all encrypted fields in a Firestore document
 * Useful for displaying data after fetching from database
 * @param {Object} doc - The document data from Firestore
 * @returns {Promise<Object>} - Document with decrypted fields
 */
export const decryptDocument = async (doc) => {
  if (!doc || typeof doc !== 'object') {
    return doc;
  }
  
  const decrypted = { ...doc };
  
  for (const [key, value] of Object.entries(decrypted)) {
    if (typeof value === 'string' && value.startsWith('ENC:')) {
      try {
        decrypted[key] = await decryptValue(value);
      } catch (error) {
        console.error(`Failed to decrypt field ${key}:`, error);
        decrypted[key] = '[Decryption Error]';
      }
    } else if (Array.isArray(value)) {
      decrypted[key] = await Promise.all(
        value.map(async (item) => {
          if (typeof item === 'string' && item.startsWith('ENC:')) {
            try {
              return await decryptValue(item);
            } catch {
              return '[Decryption Error]';
            }
          }
          return item;
        })
      );
    } else if (typeof value === 'object' && value !== null) {
      // Recursively decrypt nested objects (like credentials)
      decrypted[key] = await decryptDocument(value);
    }
  }
  
  return decrypted;
};

/**
 * Decrypts an array of Firestore documents
 * @param {Array} docs - Array of document data
 * @returns {Promise<Array>} - Array with decrypted documents
 */
export const decryptDocuments = async (docs) => {
  if (!Array.isArray(docs)) {
    return docs;
  }
  
  return Promise.all(docs.map(doc => decryptDocument(doc)));
};

export default {
  encryptValue,
  decryptValue,
  encryptArray,
  decryptArray,
  encryptFields,
  decryptFields,
  encryptFormData,
  encryptCredentials,
  decryptCredentials,
  decryptDocument,
  decryptDocuments,
  maskValue,
  isEncrypted,
  PHI_FIELDS,
  PII_FIELDS,
  CREDENTIAL_FIELDS
};
